import { computed, Directive, effect, ElementRef, HostListener, inject, input, Renderer2 } from '@angular/core';
import { presenceBoolean } from '@ion-ui/transforms';

type ButtonType = 'primary' | 'secondary' | 'link' | 'cta';
type ButtonSize = 'm' | 's' | 'xs';

const BASE_CLASS = 'ion-button';

@Directive({
  selector: 'button[ionButton], a[ionButton]',
  standalone: true,
  host: {
    '[attr.aria-disabled]': "disabled() || pseudoDisabled() ? 'true' : null",
    '[attr.ion-id]': 'ionId()',
    '[attr.tabindex]': 'tabIndex()',
  },
})
export class ButtonDirective {
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private renderer = inject(Renderer2);
  private appliedClasses: string[] = [];

  readonly appearance = input<ButtonType>('primary');
  readonly size = input<ButtonSize>('m');
  // Enforce strict boolean usage via bindings (e.g. [disabled]="isDisabled").
  readonly disabled = input(false, { transform: (value: unknown) => presenceBoolean(value, 'ionButton disabled') });
  /**
   * Makes a button or link look like it is disabled, but actually it is still interactable.
   * The real `disabled` property takes precedence when set to `true`.
   */
  readonly pseudoDisabled = input(false, {
    transform: (value: unknown) => presenceBoolean(value, 'ionButton pseudoDisabled'),
  });
  /** The ionId should always be set on interactable elements. Prefer attribute syntax: ion-id="yourId" */
  readonly ionId = input.required<string>({ alias: 'ion-id' });

  private readonly isAnchor = computed(() => this.hostElement.nativeElement.tagName.toLowerCase() === 'a');

  // Remove disabled anchors from tab order per WAI-ARIA practices:
  // https://www.w3.org/TR/2019/NOTE-wai-aria-practices-1.1-20190207/#kbd_disabled_controls
  readonly tabIndex = computed(() => (this.isAnchor() && this.disabled() ? -1 : null));

  private readonly buttonClasses = computed(() => [
    BASE_CLASS,
    `${BASE_CLASS}--${this.appearance()}`,
    `${BASE_CLASS}--${this.size()}`,
  ]);

  constructor() {
    effect(() => {
      const host = this.hostElement.nativeElement;
      const classes = this.buttonClasses();

      this.appliedClasses.forEach(className => this.renderer.removeClass(host, className));
      classes.forEach(className => this.renderer.addClass(host, className));
      this.appliedClasses = classes;
    });

    effect(() => {
      const host = this.hostElement.nativeElement;
      if (!this.isAnchor()) {
        if (this.disabled()) {
          // Property disables native button behavior; attribute keeps DOM/CSS/tests in sync.
          this.renderer.setProperty(host, 'disabled', true);
          this.renderer.setAttribute(host, 'disabled', '');
        } else {
          this.renderer.setProperty(host, 'disabled', false);
          // Remove attribute so it doesn't appear in DOM when enabled.
          this.renderer.removeAttribute(host, 'disabled');
        }
      }
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: Event) {
    if (!this.isAnchor() || !this.disabled()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }
}
